import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Thermometer, Minus, Plus } from "lucide-react";
import useSWR from "swr";
import Header from "@/components/Header";
import { Button, Card, Badge, TextArea } from "@/components/ui-kit";
import { createRecord, updateRecord, getMeta } from "@/lib/api";
import { getUser } from "@/lib/auth";

export default function TemperatureForm() {
  const navigate = useNavigate();
  const editRecord = useLocation().state?.editRecord;
  const { data: meta } = useSWR("meta", getMeta);
  const today  = new Date().toISOString().slice(0, 10);
  const recordDate = editRecord?.date || today;
  const signer = getUser()?.username || "";

  const isAdmin = getUser()?.rol === "admin";
  const [date, setDate]             = useState(recordDate);
  const [temps, setTemps]           = useState(editRecord?.readings
    ? Object.fromEntries(editRecord.readings.map((r) => [r.equipment_id, String(r.temperature)]))
    : {});
  const [notes, setNotes]           = useState(editRecord?.notes || "");
  const [submitting, setSubmitting] = useState(false);

  const equipment = meta?.equipment || [];
  const setTemp   = (id, v) => setTemps((p) => ({ ...p, [id]: v }));
  const step      = (id, max, delta) => {
    const cur  = parseFloat(temps[id]);
    const base = isNaN(cur) ? max : cur;
    setTemp(id, (Math.round((base + delta) * 10) / 10).toFixed(1));
  };
  const allFilled = equipment.length > 0 &&
    equipment.every((e) => temps[e.id] !== undefined && temps[e.id] !== "");

  const submit = async () => {
    if (!allFilled) return toast.error("Omple totes les temperatures");
    setSubmitting(true);
    try {
      const readings = equipment.map((e) => {
        const t = parseFloat(temps[e.id]);
        return { equipment_id: e.id, equipment_name: e.name, temperature: t,
                 max_temp: e.max_temp, status: t <= e.max_temp ? "OK" : "DEFICIENCIA" };
      });
      const payload = { date, readings, signer, notes };
      if (editRecord) await updateRecord("temperature", editRecord.id, payload);
      else await createRecord("temperature", payload);
      toast.success(editRecord ? "Temperatures modificades" : "Temperatures registrades");
      navigate("/history?type=temperature");
    } catch { toast.error("Error desant el registre"); }
    finally   { setSubmitting(false); }
  };

  return (
    <>
      <Header title="Temperatures" />
      <div className="mx-auto max-w-2xl px-4 pb-32 pt-6 sm:px-6 sm:pt-10">
        <div className="animate-fade-up">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-terracotta text-white"><Thermometer size={22} /></div>
            <div>
              <div className="label-eyebrow text-terracotta">Registre diari</div>
              <h2 className="font-display text-2xl font-bold text-ink sm:text-3xl">Control de temperatures</h2>
            </div>
          </div>
          <p className="mt-3 text-sm text-ink-soft">
            Apunta una temperatura per a cada equip. Si supera el limit, es marcara com{" "}
            <span className="font-semibold text-ember">DEFICIENCIA</span>.
          </p>
        </div>
        <div className="mt-6 space-y-4">
          {(isAdmin || editRecord) && (
            <Card>
              <div className="label-eyebrow mb-2">Data del registre {editRecord ? "" : "(admin)"}</div>
              <input type="date" value={date} onChange={(ev) => setDate(ev.target.value)} className="h-14 w-full rounded-xl border border-line bg-white px-4 text-base text-ink outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20" />
            </Card>
          )}
          {equipment.map((e) => {
            const cur   = parseFloat(temps[e.id]);
            const isDef = !isNaN(cur) && cur > e.max_temp;
            return (
              <Card key={e.id} testid={`equipment-card-${e.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display text-lg font-bold text-ink">{e.name}</div>
                    <div className="text-xs text-ink-soft">{"Limit: ≤ " + e.max_temp + "°C"}</div>
                  </div>
                  {!isNaN(cur) && <Badge status={isDef ? "DEFICIENCIA" : "OK"} />}
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <button type="button" data-testid={`temp-minus-${e.id}`}
                    onClick={() => step(e.id, e.max_temp, -0.1)}
                    className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-line bg-muted text-ink hover:bg-cream">
                    <Minus size={20} />
                  </button>
                  <div className="relative flex-1">
                    <input data-testid={`temp-input-${e.id}`} type="number" step="0.1"
                      inputMode="decimal" placeholder="--" value={temps[e.id] ?? ""}
                      onChange={(ev) => setTemp(e.id, ev.target.value)}
                      className={"h-14 w-full rounded-xl border bg-white text-center text-2xl font-bold tabular-nums text-ink outline-none transition-all focus:ring-2 " +
                        (isDef ? "border-ember ring-ember/20 text-ember"
                               : "border-line focus:border-terracotta focus:ring-terracotta/20")} />
                    <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-base font-medium text-ink-soft">C</span>
                  </div>
                  <button type="button" data-testid={`temp-plus-${e.id}`}
                    onClick={() => step(e.id, e.max_temp, 0.1)}
                    className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-line bg-muted text-ink hover:bg-cream">
                    <Plus size={20} />
                  </button>
                </div>
              </Card>
            );
          })}
          <Card>
            <div className="label-eyebrow mb-2">Observacions (opcional)</div>
            <TextArea testid="notes-input" placeholder="Per exemple: incident al mati, equip aturat..."
              value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Card>
        </div>
      </div>
      <StickySubmit onClick={submit} disabled={submitting || !allFilled}
        label={submitting ? "Desant..." : editRecord ? "Desar canvis" : "Desar registre"} />
    </>
  );
}

export function StickySubmit({ onClick, disabled, label, testid = "submit-record" }) {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-line bg-bg/95 px-4 py-3 backdrop-blur-md sm:px-6">
      <div className="mx-auto max-w-2xl">
        <Button testid={testid} onClick={onClick} disabled={disabled} className="w-full">{label}</Button>
      </div>
    </div>
  );
}
